// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";

export const CustomDashboardView = ({ user = null }: { user?: UserRow | null }) => {
  const [dashboards, setDashboards] = useState<any[]>([]);
  const [selectedDash, setSelectedDash] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [dashName, setDashName] = useState("");
  const [widgets, setWidgets] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [metricValues, setMetricValues] = useState<Record<string, any>>({});
  const [widgetData, setWidgetData] = useState<Record<string, any>>({});

  useEffect(() => {
    apiCall("/reports/dashboards").then(d => setDashboards(d.dashboards || [])).catch(() => {});
    apiCall("/reports/metrics/current").then(d => {
      setMetrics(d.metrics || []);
      const vals: Record<string, any> = {};
      (d.metrics || []).forEach((m: any) => { vals[m.metric_key] = m; });
      setMetricValues(vals);
    }).catch(() => {});
    apiCall("/reports").then(d => setReports(d.reports || [])).catch(() => {});
  }, []);

  const loadDashboard = async (dash: any) => {
    setSelectedDash(dash);
    setEditing(false);
    const data: Record<string, any> = {};
    for (const w of (dash.widgets || [])) {
      if (w.type === "report") {
        try {
          const d = await apiCall(`/reports/${w.key}/run`, { method: "POST", body: JSON.stringify({ filters: {} }) });
          data[w.key] = d;
        } catch {}
      }
    }
    setWidgetData(data);
  };

  const startCreate = () => {
    setEditing(true);
    setSelectedDash(null);
    setDashName("");
    setWidgets([]);
  };

  const startEdit = () => {
    if (!selectedDash) return;
    setEditing(true);
    setDashName(selectedDash.name);
    setWidgets([...(selectedDash.widgets || [])]);
  };

  const addWidget = (type: string, key: string) => {
    if (widgets.some((w: any) => w.type === type && w.key === key)) return;
    setWidgets(prev => [...prev, { type, key, width: 1, order: prev.length }]);
  };

  const removeWidget = (idx: number) => setWidgets(prev => prev.filter((_: any, i: number) => i !== idx));
  const moveWidget = (idx: number, dir: any) => {
    setWidgets(prev => {
      const arr = [...prev];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return arr.map((w: any, i: number) => ({ ...w, order: i }));
    });
  };
  const toggleWidth = (idx: number) => setWidgets(prev => prev.map((w: any, i: number) => i === idx ? { ...w, width: w.width === 2 ? 1 : 2 } : w));

  const saveDashboard = async () => {
    if (!dashName.trim()) return;
    try {
      if (selectedDash) {
        await apiCall(`/reports/dashboards/${selectedDash.id}`, { method: "PUT", body: JSON.stringify({ name: dashName, widgets }) });
        setDashboards(prev => prev.map((d: any) => d.id === selectedDash.id ? { ...d, name: dashName, widgets } : d));
        setSelectedDash({ ...selectedDash, name: dashName, widgets });
      } else {
        const d = await apiCall("/reports/dashboards", { method: "POST", body: JSON.stringify({ name: dashName, widgets }) });
        if (d.dashboard) { setDashboards(prev => [...prev, d.dashboard]); setSelectedDash(d.dashboard); }
      }
      setEditing(false);
    } catch { alert("Failed to save dashboard"); }
  };

  const deleteDashboard = async (id: string) => {
    if (!window.confirm("Delete this dashboard?")) return;
    await apiCall(`/reports/dashboards/${id}`, { method: "DELETE" });
    setDashboards(prev => prev.filter((d: any) => d.id !== id));
    if (selectedDash?.id === id) { setSelectedDash(null); }
  };

  const fmtMetricVal = (m: any) => {
    if (!m || m.currentValue == null) return "\u2014";
    if (m.format === "percent") return `${m.currentValue}%`;
    if (m.format === "days") return `${m.currentValue}d`;
    return Number(m.currentValue).toLocaleString();
  };

  const bInp: CSSProperties = { padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

  if (editing) {
    return (
      <div style={{ backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 10, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{selectedDash ? "Edit Dashboard" : "Create Dashboard"}</div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: T.txM, display: "block", marginBottom: 4 }}>Dashboard Name</label>
          <input value={dashName} onChange={e => setDashName(e.target.value)} placeholder="My Dashboard" style={bInp} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.txM, marginBottom: 8 }}>Add Metrics</div>
            <div style={{ maxHeight: 200, overflow: "auto", border: `1px solid ${T.brd}`, borderRadius: 6, padding: 8 }}>
              {metrics.map((m: any) => (
                <label key={m.metric_key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={widgets.some((w: any) => w.type === "metric" && w.key === m.metric_key)} onChange={() => widgets.some((w: any) => w.type === "metric" && w.key === m.metric_key) ? removeWidget(widgets.findIndex((w: any) => w.type === "metric" && w.key === m.metric_key)) : addWidget("metric", m.metric_key)} />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.txM, marginBottom: 8 }}>Add Reports</div>
            <div style={{ maxHeight: 200, overflow: "auto", border: `1px solid ${T.brd}`, borderRadius: 6, padding: 8 }}>
              {reports.map(r => (
                <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={widgets.some((w: any) => w.type === "report" && w.key === r.id)} onChange={() => widgets.some((w: any) => w.type === "report" && w.key === r.id) ? removeWidget(widgets.findIndex((w: any) => w.type === "report" && w.key === r.id)) : addWidget("report", r.id)} />
                  {r.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        {widgets.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.txM, marginBottom: 8 }}>Widget Order & Size</div>
            {widgets.map((w: any, i: number) => {
              const label = w.type === "metric" ? (metrics.find((m: any) => m.metric_key === w.key)?.label || w.key) : (reports.find(r => r.id === w.key)?.name || w.key);
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", border: `1px solid ${T.brd}`, borderRadius: 4, marginBottom: 4, backgroundColor: "#fff" }}>
                  <span style={{ fontSize: 9, color: T.txM, backgroundColor: w.type === "metric" ? T.accS : T.bluS, padding: "2px 6px", borderRadius: 3, fontWeight: 600 }}>{w.type}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>{label}</span>
                  <button onClick={() => toggleWidth(i)} style={{ padding: "2px 8px", border: `1px solid ${T.brd}`, borderRadius: 3, fontSize: 10, cursor: "pointer", fontFamily: "inherit", backgroundColor: "#fff" }}>{w.width === 2 ? "Full" : "Half"}</button>
                  <button onClick={() => moveWidget(i, -1)} disabled={i === 0} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: T.txM }}>{"\u25B2"}</button>
                  <button onClick={() => moveWidget(i, 1)} disabled={i === widgets.length - 1} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: T.txM }}>{"\u25BC"}</button>
                  <button onClick={() => removeWidget(i)} style={{ background: "none", border: "none", cursor: "pointer", color: T.red, fontSize: 14 }}>{"\u00D7"}</button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { saveDashboard(); }} disabled={!dashName.trim()} style={{ padding: "8px 20px", backgroundColor: dashName.trim() ? T.acc : T.txM, color: "#fff", border: "none", borderRadius: 6, cursor: dashName.trim() ? "pointer" : "default", fontWeight: 600, fontSize: 13, fontFamily: "inherit" }}>Save Dashboard</button>
          <button onClick={() => setEditing(false)} style={{ padding: "8px 16px", backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Cancel</button>
        </div>
      </div>
    );
  }

  // Render selected dashboard
  if (selectedDash) {
    const dw = selectedDash.widgets || [];
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{selectedDash.name}</h3>
            <div style={{ fontSize: 12, color: T.txM }}>{dw.length} widget{dw.length !== 1 ? "s" : ""}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={startEdit} style={{ padding: "6px 14px", backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Edit</button>
            <button onClick={() => setSelectedDash(null)} style={{ padding: "6px 14px", backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Back</button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {dw.map((w: any, i: number) => (
            <div key={i} style={{ gridColumn: w.width === 2 ? "span 2" : "span 1", backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 8, padding: 16 }}>
              {w.type === "metric" && (() => {
                const m = metricValues[w.key];
                return m ? (
                  <div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: T.tx }}>{fmtMetricVal(m)}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>{m.description}</div>
                  </div>
                ) : <div style={{ color: T.txM, fontSize: 12 }}>Metric: {w.key}</div>;
              })()}
              {w.type === "report" && (() => {
                const rd = widgetData[w.key];
                const rDef = reports.find(r => r.id === w.key);
                return (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{rDef?.name || "Report"}</div>
                    {rd?.data?.length > 0 ? (
                      <div style={{ overflowX: "auto", maxHeight: 300 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                          <thead><tr>{Object.keys(rd.data[0]).slice(0, 6).map(k => <th key={k} style={{ padding: "6px 8px", textAlign: "left", borderBottom: `2px solid ${T.brd}`, fontSize: 10, textTransform: "uppercase", backgroundColor: T.bg2 }}>{k.replace(/_/g, " ")}</th>)}</tr></thead>
                          <tbody>{rd.data.slice(0, 10).map((row: any, ri: number) => <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? "#fff" : T.bg2 }}>{Object.keys(rd.data[0]).slice(0, 6).map(k => <td key={k} style={{ padding: "5px 8px", borderBottom: `1px solid ${T.brd}`, whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{row[k] == null ? "" : String(row[k]).split("T")[0]}</td>)}</tr>)}</tbody>
                        </table>
                        {rd.totalRows > 10 && <div style={{ fontSize: 10, color: T.txM, marginTop: 6 }}>Showing 10 of {rd.totalRows} rows</div>}
                      </div>
                    ) : <div style={{ color: T.txM, fontSize: 12, padding: 12 }}>No data</div>}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Dashboard list
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: T.txM }}>{dashboards.length} dashboard{dashboards.length !== 1 ? "s" : ""}</div>
        <button onClick={startCreate} style={{ padding: "6px 14px", backgroundColor: T.acc, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>+ New Dashboard</button>
      </div>
      {dashboards.length === 0 && <div style={{ textAlign: "center", padding: 40, color: T.txM }}>No dashboards yet. Create one to get started.</div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        {dashboards.map(d => (
          <div key={d.id} onClick={() => loadDashboard(d)} style={{ padding: 16, backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 8, cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{d.name}</div>
              <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); deleteDashboard(d.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.red, fontSize: 14 }}>{"\u00D7"}</button>
            </div>
            <div style={{ fontSize: 11, color: T.txM, marginTop: 4 }}>{(d.widgets || []).length} widget{(d.widgets || []).length !== 1 ? "s" : ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

